import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import HoverTooltip from '../../common/HoverTooltip/HoverTooltip';

export interface EmailBadgeInput {
  verification_status?: string;
  confidence_level?: string;
  is_catch_all?: boolean;
  is_role_account?: boolean;
}

interface EmailBadgeProps {
  email: EmailBadgeInput;
  size?: 'sm' | 'md';
}

const BASE = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold flex-shrink-0';

const CONFIDENCE_STYLES: Record<string, string> = {
  HIGH: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  LOW: 'bg-gray-50 text-gray-600 border-gray-200',
};

const CONFIDENCE_DOT: Record<string, string> = {
  HIGH: 'text-emerald-500',
  MEDIUM: 'text-amber-500',
  LOW: 'text-gray-400',
};

const CONFIDENCE_TOOLTIPS: Record<string, string> = {
  HIGH: 'High confidence — pulled directly from the business website',
  MEDIUM: 'Medium confidence — guessed from naming pattern. Verify before sending',
  LOW: 'Low confidence — found via indirect sources. High bounce risk if unverified',
};

const EmailBadge: React.FC<EmailBadgeProps> = ({ email, size = 'sm' }) => {
  const iconSize = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';

  if (email.is_catch_all === true) {
    return (
      <HoverTooltip content="Domain accepts any address — verification cannot confirm a specific inbox">
        <span className={`${BASE} bg-amber-50 text-amber-700 border-amber-200`}>
          <AlertTriangle className={iconSize} />
          Catch-all domain
        </span>
      </HoverTooltip>
    );
  }

  switch (email.verification_status) {
    case 'GUARANTEED':
      return (
        <span className="inline-flex items-center gap-1">
          <HoverTooltip content="Live inbox confirmed">
            <span className={`${BASE} bg-emerald-50 text-emerald-700 border-emerald-200`}>
              <CheckCircle className={iconSize} />
              Verified
            </span>
          </HoverTooltip>
          {email.is_role_account && (
            <HoverTooltip content="Shared role or department inbox">
              <span className={`${BASE} border-gray-300 text-gray-500 bg-gray-50`}>
                Generic alias
              </span>
            </HoverTooltip>
          )}
        </span>
      );

    case 'INBOX_FULL':
      return (
        <HoverTooltip content="Inbox exists but cannot currently accept new messages. You can verify it again after 7 days.">
          <span className={`${BASE} bg-amber-50 text-amber-700 border-amber-200`}>
            <AlertTriangle className={iconSize} />
            Inbox full
          </span>
        </HoverTooltip>
      );

    case 'RISKY':
      return (
        <span className="inline-flex items-center gap-1">
          <HoverTooltip content="Verification was inconclusive or the address has elevated delivery risk.">
            <span className={`${BASE} bg-amber-50 text-amber-700 border-amber-200`}>
              <AlertTriangle className={iconSize} />
              Risky
            </span>
          </HoverTooltip>
          {email.is_role_account && (
            <HoverTooltip content="Shared role or department inbox">
              <span className={`${BASE} border-gray-300 text-gray-500 bg-gray-50`}>
                Generic alias
              </span>
            </HoverTooltip>
          )}
        </span>
      );

    case 'INVALID':
      return (
        <HoverTooltip content="Invalid address — not deliverable. Credit was refunded.">
          <span className={`${BASE} bg-red-50 text-red-700 border-red-200`}>
            <XCircle className={iconSize} />
            Invalid
          </span>
        </HoverTooltip>
      );

    case 'UNKNOWN':
      return (
        <HoverTooltip content="Provider couldn't decide last time — usually transient. Try again to retry.">
          <span className={`${BASE} bg-gray-100 text-gray-600 border-gray-200 cursor-help`}>
            <HelpCircle className={iconSize} />
            Unconfirmed
          </span>
        </HoverTooltip>
      );

    case 'GENERIC':
      return (
        <HoverTooltip content="Shared inbox (e.g. info@, hello@). Lower reply rates than personal emails.">
          <span className={`${BASE} border-gray-300 text-gray-500 bg-gray-50`}>
            Generic alias
          </span>
        </HoverTooltip>
      );

    default: {
      if (!email.confidence_level) return null;
      const level = email.confidence_level.toUpperCase();
      const label = email.confidence_level.charAt(0).toUpperCase() + email.confidence_level.slice(1).toLowerCase();
      return (
        <HoverTooltip content={CONFIDENCE_TOOLTIPS[level] ?? CONFIDENCE_TOOLTIPS.LOW}>
          <span className={`${BASE} ${CONFIDENCE_STYLES[level] ?? CONFIDENCE_STYLES.LOW}`}>
            <span className={CONFIDENCE_DOT[level] ?? CONFIDENCE_DOT.LOW}>●</span>
            {label}
          </span>
        </HoverTooltip>
      );
    }
  }
};

interface CatchAllBadgeProps {
  tone?: 'default' | 'subtle';
}

export const CatchAllBadge: React.FC<CatchAllBadgeProps> = ({ tone = 'default' }) => {
  const isSubtle = tone === 'subtle';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border flex-shrink-0 ${
        isSubtle
          ? 'px-1.5 py-0.5 text-[10px] font-medium bg-amber-50/70 text-amber-600 border-amber-100'
          : 'px-2 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 border-amber-200'
      }`}
    >
      <AlertTriangle className={isSubtle ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      Catch-all domain
    </span>
  );
};

export default EmailBadge;
